import React, { useState } from 'react';

/**
 * Modern single-image picker used in admin forms.
 *
 * Shows a large preview of the current image with overlay actions:
 *   - Replace: choose a new file (stored as pending file, applied on Save)
 *   - Remove:  clears the image (calls onRemove for saved images)
 *
 * A locally-chosen pending file gets its own preview with a "Remove"
 * that just discards the pending selection.
 */
export default function ImageField({
  label = 'Image',
  url, // current saved image URL (or null)
  file, // pending File chosen but not yet saved
  onPick, // (File | null) => void
  onRemove, // async () => void  — deletes the saved image server-side
  hint = 'JPEG, PNG, WebP or GIF · up to 10 MB',
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const previewUrl = file ? URL.createObjectURL(file) : url;

  const pick = (e) => {
    setErr('');
    const f = e.target.files && e.target.files[0];
    if (f) onPick(f);
    e.target.value = ''; // allow re-picking the same file
  };

  const removeSaved = async () => {
    setErr('');
    setBusy(true);
    try {
      await onRemove();
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };

  return (
    <div className="admin-imgfield">
      <span className="admin-imgfield-label">{label}</span>

      {previewUrl ? (
        <div className="admin-imgfield-card">
          <img src={previewUrl} alt={label} />
          {file ? (
            <span className="admin-imgfield-badge">New — saved on “Save”</span>
          ) : null}
          <div className="admin-imgfield-actions">
            <label className="admin-imgfield-btn">
              <input type="file" accept="image/*" hidden onChange={pick} />
              {url ? 'Replace' : 'Change'}
            </label>
            {file ? (
              <button
                type="button"
                className="admin-imgfield-btn admin-imgfield-btn-danger"
                onClick={() => onPick(null)}
              >
                Discard
              </button>
            ) : (
              <button
                type="button"
                className="admin-imgfield-btn admin-imgfield-btn-danger"
                disabled={busy}
                onClick={removeSaved}
              >
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <label className="admin-imgfield-empty">
          <input type="file" accept="image/*" hidden onChange={pick} />
          <span className="admin-imgfield-plus">＋</span>
          <span className="admin-imgfield-upload">Upload image</span>
          <span className="admin-imgfield-hint">{hint}</span>
        </label>
      )}

      {err && <div className="admin-alert admin-alert-error">{err}</div>}
    </div>
  );
}
